import { useState, useEffect, useRef } from 'react';

interface CaptchaInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidate: (isValid: boolean) => void;
  isZh?: boolean;
  className?: string;
}

const CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LEN = 5;

function buildCode(): string {
  let code = '';
  for (let i = 0; i < CODE_LEN; i++) {
    code += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
  }
  return code;
}

function renderToDataURL(code: string): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 130;
    canvas.height = 46;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Background
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Noise dots
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(10,37,64,${0.08 + Math.random() * 0.12})`;
      ctx.beginPath();
      ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, Math.random() * 1.5 + 0.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Noise lines behind text
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = `rgba(10,37,64,${0.08 + Math.random() * 0.1})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.bezierCurveTo(
        Math.random() * canvas.width, Math.random() * canvas.height,
        Math.random() * canvas.width, Math.random() * canvas.height,
        Math.random() * canvas.width, Math.random() * canvas.height
      );
      ctx.stroke();
    }

    // Draw characters
    const charW = canvas.width / CODE_LEN;
    code.split('').forEach((ch, i) => {
      ctx.save();
      ctx.translate(i * charW + charW / 2, canvas.height / 2 + (Math.random() - 0.5) * 6);
      ctx.rotate((Math.random() - 0.5) * 0.45);
      const size = 20 + Math.floor(Math.random() * 6);
      ctx.font = `bold ${size}px 'Arial', sans-serif`;

      // Random dark color per char
      const hue = [210, 195, 220, 230, 200][i % 5];
      ctx.fillStyle = `hsl(${hue}, 60%, 25%)`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });

    // Noise lines on top
    for (let i = 0; i < 2; i++) {
      ctx.strokeStyle = `rgba(44,125,160,${0.12 + Math.random() * 0.1})`;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(0, Math.random() * canvas.height);
      ctx.lineTo(canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }

    return canvas.toDataURL('image/png');
  } catch {
    return '';
  }
}

export default function CaptchaInput({
  onChange,
  onValidate,
  isZh = true,
  className = '',
}: CaptchaInputProps) {
  const [code, setCode] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [input, setInput] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // Use refs so regenerate() never causes stale closure issues
  const onChangeRef = useRef(onChange);
  const onValidateRef = useRef(onValidate);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onValidateRef.current = onValidate; }, [onValidate]);

  const regenerate = () => {
    const newCode = buildCode();
    const url = renderToDataURL(newCode);
    setCode(newCode);
    setDataUrl(url);
    setInput('');
    setIsDirty(false);
    setIsValid(false);
    onChangeRef.current('');
    onValidateRef.current(false);
  };

  // Init on mount only
  useEffect(() => {
    regenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (raw: string) => {
    const upper = raw.toUpperCase().slice(0, CODE_LEN);
    setInput(upper);
    setIsDirty(true);
    onChangeRef.current(upper);

    if (upper.length === CODE_LEN) {
      const valid = upper === code;
      setIsValid(valid);
      onValidateRef.current(valid);
    } else {
      setIsValid(false);
      onValidateRef.current(false);
    }
  };

  const borderClass = isDirty
    ? isValid
      ? 'border-green-400 bg-green-50 focus:ring-green-200 text-green-700'
      : input.length === CODE_LEN
        ? 'border-red-300 bg-red-50 focus:ring-red-200 text-red-600'
        : 'border-slate-300 bg-white focus:ring-slate-200 text-slate-800'
    : 'border-slate-300 bg-white focus:ring-slate-200 text-slate-800';

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        {/* Captcha image */}
        <div className="shrink-0 rounded-lg overflow-hidden border border-slate-200" style={{ width: 130, height: 46 }}>
          {dataUrl ? (
            <img
              src={dataUrl}
              alt="captcha"
              width={130}
              height={46}
              draggable={false}
              style={{ display: 'block', userSelect: 'none', pointerEvents: 'none' }}
            />
          ) : (
            <div className="w-full h-full bg-slate-100 animate-pulse" />
          )}
        </div>

        {/* Refresh */}
        <button
          type="button"
          onClick={regenerate}
          title={isZh ? '刷新验证码' : 'Refresh CAPTCHA'}
          className="w-[46px] h-[46px] shrink-0 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-all cursor-pointer"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-refresh-line text-lg" />
          </div>
        </button>

        {/* Input */}
        <input
          type="text"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={isZh ? '输入验证码' : 'Enter code'}
          maxLength={CODE_LEN}
          autoComplete="off"
          spellCheck={false}
          className={`flex-1 h-[46px] px-3 rounded-lg border text-sm uppercase tracking-widest text-center font-semibold transition-all focus:outline-none focus:ring-2 ${borderClass}`}
        />
      </div>

      {/* Status tip */}
      {isDirty && input.length === CODE_LEN && (
        <div className={`flex items-center gap-1.5 text-xs font-medium ${isValid ? 'text-green-600' : 'text-red-500'}`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={isValid ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} />
          </div>
          {isValid
            ? (isZh ? '验证码正确' : 'CAPTCHA verified')
            : (isZh ? '验证码错误，点击刷新重试' : 'Incorrect, click refresh to try again')}
        </div>
      )}

      <p className="text-[10px] text-slate-400">
        {isZh ? '不区分大小写，看不清请点刷新' : 'Case-insensitive · click refresh if unclear'}
      </p>
    </div>
  );
}